/**
 * Form field primitives.
 *
 * Every field is a controlled input bound straight to the Zustand store. React
 * Hook Form is used for the larger entry forms where validation and field arrays
 * pay for themselves; for single fields, a direct binding is fewer moving parts
 * and keeps the live preview genuinely live.
 *
 * Accessibility rules enforced here rather than left to callers:
 *  - every input has a real <label> tied by id
 *  - hints are wired through aria-describedby
 *  - errors are announced, not just coloured
 */

import { useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'

interface BaseProps {
  label: string
  hint?: ReactNode
  error?: string
  /** Visually hides the label without removing it from the accessibility tree. */
  hideLabel?: boolean
}

export const TextField = ({
  label,
  hint,
  error,
  hideLabel,
  ...props
}: BaseProps & InputHTMLAttributes<HTMLInputElement>) => {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined

  return (
    <div className="w-full">
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'mb-1 block text-sm font-medium'}>
        {label}
      </label>
      <input
        id={id}
        className={`input input-bordered w-full ${error ? 'input-error' : ''}`}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={error ? true : undefined}
        {...props}
      />
      {hint && !error && (
        <p id={hintId} className="mt-1 text-xs opacity-70">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-error">
          {error}
        </p>
      )}
    </div>
  )
}

export const TextArea = ({
  label,
  hint,
  error,
  hideLabel,
  counter,
  ...props
}: BaseProps & { counter?: string } & TextareaHTMLAttributes<HTMLTextAreaElement>) => {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined

  return (
    <div className="w-full">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <label htmlFor={id} className={hideLabel ? 'sr-only' : 'block text-sm font-medium'}>
          {label}
        </label>
        {counter && <span className="text-xs tabular-nums opacity-60">{counter}</span>}
      </div>
      <textarea
        id={id}
        className={`textarea textarea-bordered w-full ${error ? 'textarea-error' : ''}`}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={error ? true : undefined}
        {...props}
      />
      {hint && !error && (
        <p id={hintId} className="mt-1 text-xs opacity-70">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-error">
          {error}
        </p>
      )}
    </div>
  )
}

export const SelectField = ({
  label,
  hint,
  options,
  hideLabel,
  ...props
}: BaseProps & {
  options: { value: string; label: string }[]
} & InputHTMLAttributes<HTMLSelectElement>) => {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  return (
    <div className="w-full">
      <label htmlFor={id} className={hideLabel ? 'sr-only' : 'mb-1 block text-sm font-medium'}>
        {label}
      </label>
      <select
        id={id}
        className="select select-bordered w-full"
        aria-describedby={hintId}
        {...(props as InputHTMLAttributes<HTMLSelectElement>)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint && (
        <p id={hintId} className="mt-1 text-xs opacity-70">
          {hint}
        </p>
      )}
    </div>
  )
}

export const RangeField = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  format,
}: {
  label: string
  value: number
  onChange: (value: number) => void
  min: number
  max: number
  step?: number
  format?: (value: number) => string
}) => {
  const id = useId()
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        <span className="text-xs tabular-nums opacity-70">{format ? format(value) : value}</span>
      </div>
      <input
        id={id}
        type="range"
        className="range range-sm"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  )
}

export const Toggle = ({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string
  hint?: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
}) => {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        {hint && (
          <p id={hintId} className="mt-0.5 text-xs opacity-70">
            {hint}
          </p>
        )}
      </div>
      <input
        id={id}
        type="checkbox"
        className="toggle toggle-sm shrink-0"
        checked={checked}
        aria-describedby={hintId}
        onChange={(e) => onChange(e.target.checked)}
      />
    </div>
  )
}

/**
 * Comma-or-newline separated list input. Users paste skills from job descriptions
 * in every conceivable format, so we accept all of them rather than enforcing one.
 */
export const ListField = ({
  label,
  hint,
  value,
  onChange,
  placeholder,
  rows = 2,
}: {
  label: string
  hint?: ReactNode
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  rows?: number
}) => (
  <TextArea
    label={label}
    hint={hint}
    rows={rows}
    placeholder={placeholder}
    value={value.join(', ')}
    counter={`${value.length} item${value.length === 1 ? '' : 's'}`}
    onChange={(e) =>
      onChange(
        e.target.value
          .split(/[,\n]/)
          .map((s) => s.trim())
          .filter(Boolean),
      )
    }
  />
)
